// This class provide api which maintain dependency map for datapage parameter and called only implicitly by internal api.
// For registering dependency explicitly for other usecase check cascade/manager.js

/**
 * Exposes API to create and maintain the property and their dependent properties
 * which configure datapage along with the respected context.
 * @example
 * {
 *  "app/primary_1" : {
 *      "Country": [
 *          {
 *             "propertyName": "States",
 *             "contextName": "app/primary_1",
 *             "pageReference": "caseInfo.content"
 *         }
 *      ]
 *  }
 * }
 */
class DatapageDependencyMap {
  private _dataSourceMap: { [key: string]: any };
  constructor() {
    this._dataSourceMap = {};
  }

  /**
   * Checks whether the given context present in property map and returns true if present else false.
   * @param contextName name of the context
   * @returns returns true if context present in property map else false
   * @example
   * contextName: "app/primary_1"
   */
  hasContextName(contextName: string) {
    return contextName in this._dataSourceMap;
  }

  /**
   * Add's the value to the respected context. If we don't pass the value or pass empty value
   * assign empty object to the respected context
   * @param contextName name of the context
   * @param value respected context mapping object
   */
  addContextName(contextName: string, value: { [key: string]: any }) {
    if (value && Object.keys(value).length > 0) {
      this._dataSourceMap[contextName] = value;
    } else {
      this._dataSourceMap[contextName] = {};
    }
  }

  /**
   * give the corresponding object for the context provided if it present otherwise return null
   * @param contextName name of the context
   * @returns returns the respected context object if context is present
   */
  getContextObjectByName(contextName: string): { [key: string]: any } | null {
    return contextName in this._dataSourceMap ? this._dataSourceMap[contextName] : null;
  }

  /**
   * Delete the corresponding object for the context if present
   * @param contextName name of the context
   */
  deleteContextName(contextName: string) {
    if (this.hasContextName(contextName)) {
      delete this._dataSourceMap[contextName];
    }
  }

  /**
   * Add or Update the property value to the corresponsing context provided if context already present
   * otherwise add the context, property and it's value.
   * @param contextName name of the context
   * @param propertyName name of dependent property inside the context
   * @param value respected context mapping object
   */
  addOrUpdatePropertyInContext(contextName: string, propertyName: string, value: { [key: string]: any }) {
    if (this.hasContextName(contextName)) {
      const contextObject = this.getContextObjectByName(contextName);

      if (contextObject) {
        if (Array.isArray(value)) {
          contextObject[propertyName] = value;
        } else {
          const previousValues = contextObject[propertyName];
          // const map = new Map();
          // previousValues.forEach(element => {
          //     map.set(element.propertyName, element)
          // });
          // contextObject[propertyName] = [...map.values(), value]
          contextObject[propertyName] = [...new Set([...previousValues, value])];
        }
      }
    } else {
      const newObject: { [key: string]: any } = {};
      if (Array.isArray(value)) {
        newObject[propertyName] = value;
      } else {
        newObject[propertyName] = [value];
      }
      this.addContextName(contextName, newObject);
    }
  }

  /**
   * Delete the property present inside the context
   * @param contextName name of the context
   * @param propertyName name of dependent property inside the context
   */
  deletePropertyInContext(contextName: string, propertyName: string) {
    if (this.hasContextName(contextName)) {
      const contextObject = this.getContextObjectByName(contextName);

      if (contextObject && propertyName in contextObject) {
        delete contextObject[propertyName];
      }
    }
  }

  /**
   * get the property from the corresponding context if it presents otherwise return null
   * @param contextName name of the context
   * @param propertyName name of dependent property inside the context
   */
  getPropertyFromContext(contextName: string, propertyName: string) {
    if (this.hasContextName(contextName)) {
      const contextObject = this.getContextObjectByName(contextName);
      if (contextObject && propertyName in contextObject) {
        return contextObject[propertyName];
      }
    }
    return [];
  }

  /**
   * get all the dependencies associated with the property
   * @param context name of the context
   * @param propertyName name of dependent property inside the context
   * @returns dependent properties array
   */
  getDependencies(context: string, propertyName: string) {
    const dependencies: string[] = this.getPropertyFromContext(context, propertyName);
    const allDependencies = new Set(dependencies);
    /*  const getInnerDependencies = (propName) => {
          this.getPropertyFromContext(context, propName).forEach((dependency) => {
            allDependencies.add(dependency);
            getInnerDependencies(dependency);
          });
        }; */
    dependencies.forEach((dependency: string) => {
      allDependencies.add(dependency);
      //   getInnerDependencies(dependency);
    });
    return [...allDependencies];
  }
}

export default new DatapageDependencyMap();
